'use client';
import styles from './style.module.scss';
import clsx from 'clsx';
import {motion} from 'framer-motion';
import {handleLogin, handleUserInfo, loginState} from '@/utils/event/util';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import globalDomains from '@/utils/global-domains';
import {useEffect, useMemo, useState} from 'react';
import {isMobile, isMacOs} from 'react-device-detect';
import {throwDialog} from '@/utils/ui/dialog';
import FreecshotPlusEventApi from '@/api/events/freecshot-plus';

const FreecshotPlus = () => {
  /** 로그인 한 사용자의 상태 */
  const {userId} = useMemo(() => handleUserInfo(), []);

  /**
   * isLogin: bool, : 로그인 되어있으면 true
   * isLimit: bool, : 현재 참여자가 2000명 미만이면 true, 2000명 이상 이면 false
   * isEventActive: bool, : 이벤트 기간이 맞으면 true
   * isFirstCome: bool, : 선착순 이벤트 참여 여부 , 참여 했으면 true, 참여 안했으면 false
   * isMessageSent : bool : 메시지 이벤트 참여 여부 , 참여 했으면 true, 참여 안했으면 false
   **/
  const [eventStatus, setEventStatus] = useState(null);
  const [userNickname, setUserNickname] = useState('');
  const [errorCode, setErrorCode] = useState({
    code: null,
    message: null,
  });

  useEffect(() => {
    const getEventStatus = async () => {
      const response = await FreecshotPlusEventApi.getEventStatus({
        work: 'GET_EVENT_STATUS',
      });
      if (response.result > 0) {
        setEventStatus(response.data);
        setUserNickname(response.data.nickName);
      }
    };
    getEventStatus();
  }, []);

  /** 방송하기 버튼 눌렀을 때 */
  const handleOnBroadcast = () => {
    if (isMobile) {
      throwDialog(
        'Message',
        {
          message:
            '이 기능은 모바일에서는 제공되지 않습니다.\nPC 환경에서 이용해 주세요.',
        },
        {
          isCustomDialog: true,
          hasCloseButtonOnly: true,
          isConfirm: true,
        },
      );
      return;
    }
    if (isMacOs) {
      throwDialog(
        'Message',
        {
          message:
            '<strong>Mac에서는 설치할 수 없습니다.</strong>\n프릭샷 플러스는 Windows에서만 사용 가능합니다.\n이용을 원하실 경우 Windows PC에서 설치해 주시기 바랍니다.',
        },
        {
          isCustomDialog: true,
          hasCloseButtonOnly: true,
          isConfirm: true,
        },
      );
      return;
    }
    openSoopStudio();
  };

  const openSoopStudio = () => {
    window.open(
      `https://update.sooplive.com/FreecShotPlus_installer.exe`,
      '_self',
    );
  };

  /** 이벤트 버튼 눌렀을 때 */
  const handleGetApply = () => {
    if (!loginState) {
      handleLogin();
      return;
    }
    //선착순 마감 에러 코드
    setErrorCode({code: -104, message: null});
  };

  /** 스크롤 이동 */
  const handleScrollTo = (targetId) => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  };

  const handleGetProduct = async () => {
    if (!loginState) {
      handleLogin();
      return;
    }
    try {
      const response = await FreecshotPlusEventApi.getEventStatus({
        work: 'POST_EVENT_APPLY',
      });
      if (response?.result) {
        setErrorCode({code: response.result, message: response?.message});
      }
    } catch (error) {
      setErrorCode({
        code: null,
        message: null,
      });
    }
  };

  const handleShowMessageForm = async () => {
    if (!loginState) {
      handleLogin();
      return;
    }
    try {
      const response = await FreecshotPlusEventApi.getEventStatus({
        work: 'CHECK_EVENT_MESSAGE',
      });
      if (response?.result === 1) {
        throwDialog(
          'event/FreecshotPlusMessage',
          {userNickname},
          {isCustomDialog: true},
        );
      } else {
        setErrorCode({code: response.result, message: response?.message});
      }
    } catch (error) {
      setErrorCode({
        code: null,
        message: null,
      });
    }
  };

  useEffect(() => {
    if (errorCode.code) {
      throwDialog(
        'event/FreecshotPlus',
        {eventStatus: errorCode, handleOnBroadcast, handleScrollTo},
        {
          isCustomDialog: true,
        },
      );
    }
    return () => {
      setErrorCode({code: null, message: null});
    };
  }, [errorCode.code]);

  const renderEventButton = () => {
    // 1. 로그인하지 않은 경우
    if (!loginState) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.receive)}
          onClick={handleLogin}>
          로그인
        </button>
      );
    }

    // 2. 이벤트가 비활성화된 경우
    if (!eventStatus?.isEventActive) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.disabled)}>
          종료된 이벤트 입니다
        </button>
      );
    }

    // 3. 이미 참여한 경우
    if (eventStatus?.isFirstCome) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.disabled)}>
          이벤트 참여 완료
        </button>
      );
    }

    // 4. 선착순 마감된 경우 (상품 받기)
    if (eventStatus?.isLimit) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.receive)}
          onClick={handleGetProduct}>
          상품 받기
        </button>
      );
    }

    // 5. 추첨 경품 응모하기
    return (
      <button
        type='button'
        className={clsx(styles.broadcast_action, styles.apply)}
        onClick={handleGetApply}>
        추첨 경품 응모하기
      </button>
    );
  };

  const renderMessageButton = () => {
    // 1. 로그인하지 않은 경우
    if (!loginState) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.receive)}
          onClick={handleLogin}>
          로그인
        </button>
      );
    }
    //2. 메시지 보내기 참여 여부
    if (!eventStatus?.isMessageSent) {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.receive)}
          onClick={handleShowMessageForm}>
          메세지 보내기
        </button>
      );
    } else {
      return (
        <button
          type='button'
          className={clsx(styles.broadcast_action, styles.disabled)}>
          이벤트 참여 완료
        </button>
      );
    }
  };

  return (
    <div id={styles.wrap}>
      <header className={styles.header}>
        <div className={styles.inner}>
          <h1>
            <a href={`${globalDomains.WWW_AFREECATV}`} target='_blank'>
              SOOP
            </a>
          </h1>
          <div className={styles.login_wrap}>
            {loginState ? (
              <>
                <div className={styles.user}>
                  <MyProfileImg userId={userId} className={styles.thumb} />
                  <span className={styles.nick}>{userNickname}</span>
                </div>
                <button
                  type='button'
                  className={styles.logout}
                  onClick={handleLogin}>
                  로그아웃
                </button>
              </>
            ) : (
              <button
                type='button'
                className={styles.login}
                onClick={handleLogin}>
                로그인
              </button>
            )}
          </div>
        </div>
      </header>

      <div className={styles.visual_area}>
        <div className={styles.inner}>
          <div className={styles.title_box}>
            <h2>프릭샷? 이제는 플러스야</h2>
            <p className={styles.title_text}>
              <em>FreecShot Plus</em>로 방송하고{' '}
              <strong>
                선착순 당첨받자<i className={styles.italic}>!</i>
              </strong>
            </p>
            <span className={styles.date}>9/18(목) ~ 10/17(금)</span>
          </div>
          <span className={styles.obj}>
            <motion.i
              className={styles.deco}
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{delay: 2, duration: 0.5}}
            />
          </span>
          <button
            type='button'
            onClick={handleOnBroadcast}
            className={styles.btn_broadcast}>
            <em>FreecShot Plus</em>로 방송하기
          </button>
          <ul className={styles.event_summary}>
            <li className={styles.product_item1}>
              <em className={styles.num}>01</em>
              <i className={styles.product_img}></i>
              <span>
                <em>2시간만</em>
                <br />
                방송하면
                <br />
                <strong>컴포즈 아.아 당첨</strong>
              </span>
            </li>
            <li className={styles.product_item2}>
              <em className={styles.num}>02</em>
              <i className={styles.product_img}></i>
              <span>
                <em>칭찬 한마디</em>
                <br />
                작성하면
                <br />
                <strong>
                  추첨으로 상품까지<i className={styles.italic}>!</i>
                </strong>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.contents_area}>
        <section className={styles.section1}>
          <div className={styles.inner}>
            <motion.h3
              className={styles.title}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3}}
              viewport={{once: true}}>
              이젠 선택이 아닌 필수! FreecShot Plus로 2시간 방송하면 아.아
              선착순 당첨!
            </motion.h3>
            <span className={styles.obj}>
              <motion.i
                className={styles.deco}
                initial={{opacity: 0, scale: 0}}
                whileInView={{opacity: 1, scale: [0, 1.5, 1]}}
                transition={{delay: 0.3, duration: 0.7}}
                viewport={{once: true}}
              />
            </span>
            <motion.div
              className={styles.reward_spec}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3, delay: 0.3}}
              viewport={{once: true}}>
              <div className={styles.spec_list}>
                <dl>
                  <dt>대상</dt>
                  <dd>총 누적 방송 시간 20시간 이상 스트리머</dd>
                </dl>
                <dl>
                  <dt>참여 기간</dt>
                  <dd>9/18(목) ~ 10/17(금)</dd>
                </dl>
                <dl>
                  <dt>당첨 결과</dt>
                  <dd>
                    선착순 당첨
                    <span>이벤트 참여 후 즉시 확인 가능</span>
                    추첨 상품
                    <span>11월 중, 프릭샷 플러스 방송국 게시글 발표</span>
                  </dd>
                </dl>
                <dl>
                  <dt>발송</dt>
                  <dd>선착순 당첨 & 추첨 상품, 11월 중 발급</dd>
                </dl>
              </div>
              {/* 이벤트 종류/유무 체크 */}
              {renderEventButton()}
              <ul className={styles.notice_list}>
                <li>- 프릭샷 플러스로 방송한 시간만 집계됩니다.</li>
                <li>- PC에서만 가능 / 2,000명 선착순 마감 시 조기 종료</li>
              </ul>
              <a
                href={`${globalDomains.RES_AFREECATV}/eventzone/event/2025/freecshot_plus/FreecShotPlus_guide.pdf`}
                className={styles.guide_download}
                target='_blank'
                download>
                프릭샷 플러스 방송 가이드 다운로드
              </a>
            </motion.div>
          </div>
        </section>

        <section id='messageSection' className={styles.section2}>
          <div className={styles.inner}>
            <motion.h3
              className={styles.title}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3}}
              viewport={{once: true}}>
              보너스 이벤트 FreecShot Plus 사용 후 칭찬의 메세지를 남겨주시면
              추첨에 자동 응모됩니다.
            </motion.h3>
            <span className={styles.obj}>
              <motion.i
                className={styles.light}
                initial={{opacity: 0}}
                whileInView={{opacity: 1}}
                transition={{delay: 1, duration: 0.5}}
              />
              <motion.i
                className={styles.deco}
                initial={{opacity: 0, scale: 0}}
                whileInView={{opacity: 1, scale: [0, 1.3, 1]}}
                transition={{delay: 0.3, duration: 0.8}}
                viewport={{once: true}}
              />
            </span>
            <motion.div
              className={styles.prize_list}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3, delay: 0.3}}
              viewport={{once: true}}>
              <ul>
                <li>
                  <em className={styles.rank}>1등</em>
                  <span>
                    LG 울트라기어 게이밍 모니터 <em>(1명)</em>
                  </span>
                </li>
                <li>
                  <em className={styles.rank}>2등</em>
                  <span>
                    Shure 팟캐스트 XLR 듀얼 마이크 <em>(1명)</em>
                  </span>
                </li>
                <li>
                  <em className={styles.rank}>3등</em>
                  <span>
                    Razer Viper V3 페이커 에디션 <em>(1명)</em>
                  </span>
                </li>
              </ul>
              {renderMessageButton()}
            </motion.div>
            <motion.h3
              className={styles.title2}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3}}
              viewport={{once: true}}>
              프릭샷 플러스를 꼭 써야하는 이유!
            </motion.h3>
            <motion.p
              className={styles.reason}
              initial={{y: 60, opacity: 0}}
              whileInView={{y: 0, opacity: 1}}
              transition={{duration: 0.3, delay: 0.3}}
              viewport={{once: true}}>
              더 가벼워진 리소스, 더 빨라진 속도, 세밀한 오디오 채널 관리, 2배
              늘어난 기능, 편리한 설정 가져오기 기능 제공
            </motion.p>
          </div>
        </section>
      </div>

      <div className={styles.notice_area}>
        <div className={styles.inner}>
          <strong className={styles.title}>
            <em>이벤트</em> 유의사항
          </strong>
          <ul className={styles.list}>
            <li>이벤트가 시작되는 오후 2시부터 방송 시간이 집계 됩니다.</li>
            <li>프릭샷 플러스로 방송된 시간만 인정됩니다.</li>
            <li>
              누적 방송 시간은 방송 종료 시점에 집계되므로
              <br /> 이벤트 기간 중 누적 방송 시간을 달성하는 경우,
              <br /> 반드시 방송을 종료해야 반영됩니다.
            </li>
            <li>
              상품 수량 소진 시 이벤트가 조기 마감 될 수 있는 점 참고
              부탁드립니다.
            </li>
            <li>
              당첨자는 11월 중 쪽지를 통해 개별 안내드릴 예정입니다.
              <br />
              사전에 쪽지 수신 설정을 허용해 주세요.
            </li>
            <li>
              당첨자 발표일 및 경품 발송 일정은 당사 사정에 따라 변경될 수
              있습니다.
            </li>
            <li>
              잘못된 정보로 인해 개별 연락이 되지 않을 경우,
              <br />
              당첨이 취소될 수 있으며, 재발송이 불가능합니다.
            </li>
            <li>
              경품 지급을 위해 필요한 정보를 요청드릴 수 있으며,
              <br />
              정보 미제공 시 지급이 제한될 수 있습니다.
            </li>
            <li>
              5만 원 이상 경품 당첨 시,
              <br />
              소비자 부담의 제세공과금 22% 납부가 필요합니다.
            </li>
            <li>
              당첨된 경품은 양도·교환·재발송이 불가하며,
              <br />
              분실·반송에 대한 책임은 당사에서 지지 않습니다.
            </li>
            <li>
              경품은 공급처 사정에 따라 동등한 금액의
              <br />
              다른 상품으로 대체될 수 있으며, 사전 안내 후 진행됩니다.
            </li>
          </ul>
        </div>
      </div>

      <footer className={styles.footer}>ⓒ SOOP Corp.</footer>
    </div>
  );
};

export default FreecshotPlus;
